import { useEffect } from 'react'
import { useQuery } from '@tanstack/react-query'
import api from 'api/api'
import { cacheUser } from 'state/cache'
import { USER_ENTITY_QUERY_POLICY } from 'api/queryPolicies'

type Options = {
    enabled?: boolean
    refetchOnMount?: boolean | 'always'
}

export default function useGetUser(userId: string | undefined, options: Options = {}) {
    const { enabled = true, refetchOnMount = true } = options

    const result = useQuery({
        queryKey: ['user', userId],

        queryFn: async () => {
            if (!userId) return null
            return await api.getUser(userId)
        },
        // Note: onSuccess is deprecated in react-query v5; using useEffect instead

        enabled: !!userId && enabled,
        gcTime: USER_ENTITY_QUERY_POLICY.gcTime,
        staleTime: USER_ENTITY_QUERY_POLICY.staleTime,
        refetchOnMount,
        refetchOnWindowFocus: false,
        refetchOnReconnect: false,
        structuralSharing: false,
        networkMode: 'always',
    })

    // Populate the normalized user cache when data is fetched
    // (replaces deprecated onSuccess callback)
    useEffect(() => {
        try {
            if (result.data?.id) {
                cacheUser(result.data, true)
            }
        } catch {}
    }, [result.data])

    return {
        ...result,
        user: result.data,
    }
}
